/**
 * Eric (2026-05-16) — Platform BYOK Overview (founder-only).
 *
 * Vue cross-org du statut Forfait AI : qui a configuré quoi, qui consomme
 * combien. Utilisé pour suivre les clients Knoria SaaS et anticiper les
 * besoins de support ou de configuration.
 *
 * Accès : utilisateurs avec `is_founder=true` uniquement. Le backend
 * (`GET /api/platform/byok-overview`) renvoie un 403 sinon.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LanguageContext";
import { canAccessPlatform } from "../utils/permissions";
import CostByOrgCard from "../components/admin/CostByOrgCard"; // Tache 1293
import {
  Buildings, ShieldCheck, Warning, CheckCircle, ArrowSquareOut,
  SpinnerGap, Robot, Microphone, CloudArrowUp, ChartLine,
} from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";

const SERVICE_META = {
  openai: { icon: Robot, label: "OpenAI", color: "violet" },
  assemblyai: { icon: Microphone, label: "AssemblyAI", color: "amber" },
  ovh_storage: { icon: CloudArrowUp, label: "OVHcloud", color: "emerald" },
};

function ServiceCell({ service, info, lang }) {
  const Meta = SERVICE_META[service] || {};
  const Icon = Meta.icon;
  if (!info?.configured) {
    return (
      <div
        className="flex items-center gap-1.5 text-[11px] text-rose-500"
        data-testid={`byok-cell-${service}-missing`}
        title={lang === "fr" ? "Non configuré" : "Not configured"}
      >
        <Warning size={13} weight="bold" /> {lang === "fr" ? "Manquant" : "Missing"}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-0.5" data-testid={`byok-cell-${service}-ok`}>
      <div className="flex items-center gap-1.5 text-[11px] text-teal-600 font-medium">
        {Icon && <Icon size={13} weight="bold" />}
        <span>{info.key_masked}</span>
      </div>
      {info.updated_by && (
        <span className="text-[9px] text-zinc-400 truncate max-w-[150px]" title={info.updated_by}>
          {info.updated_by}
        </span>
      )}
    </div>
  );
}

export default function PlatformBYOKOverviewPage() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | complete | missing

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/platform/byok-overview`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || (lang === "fr" ? "Erreur" : "Error"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const filteredOrgs = useMemo(() => {
    if (!data?.organizations) return [];
    if (filter === "complete") return data.organizations.filter(o => o.byok.complete);
    if (filter === "missing") return data.organizations.filter(o => !o.byok.complete);
    return data.organizations;
  }, [data, filter]);

  if (!canAccessPlatform(user)) {
    return (
      <div className="p-8 text-center" data-testid="byok-overview-forbidden">
        <ShieldCheck size={48} className="mx-auto text-rose-500 mb-3" />
        <h2 className="text-xl font-semibold mb-1">
          {lang === "fr" ? "Accès réservé aux fondateurs" : "Founder access only"}
        </h2>
        <p className="text-sm text-muted-foreground">
          {lang === "fr"
            ? "Cette page est réservée à l'équipe plateforme Knoria."
            : "This page is restricted to the Knoria platform team."}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center gap-2 text-muted-foreground" data-testid="byok-overview-loading">
        <SpinnerGap size={20} className="animate-spin" /> {lang === "fr" ? "Chargement…" : "Loading…"}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-rose-600" data-testid="byok-overview-error">
        {error}
      </div>
    );
  }

  const s = data?.summary || {};

  return (
    <div className="space-y-6" data-testid="byok-overview-page">
      <div>
        <h1 className="text-2xl font-bold mb-1 flex items-center gap-2">
          <Buildings size={26} weight="duotone" className="text-[#002FA7]" />
          {lang === "fr" ? "Vue d'ensemble Forfait AI (Plateforme)" : "Platform AI Package Overview"}
        </h1>
        <p className="text-sm text-muted-foreground">
          {lang === "fr"
            ? "Statut BYOK et consommation 30 jours de chaque organisation. Permet à l'équipe plateforme de suivre l'adoption et d'anticiper le support."
            : "BYOK status and 30-day consumption per org. Helps the platform team track adoption and anticipate support."}
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryCard
          label={lang === "fr" ? "Organisations" : "Organizations"}
          value={s.total_orgs}
          icon={Buildings}
          color="blue"
          testid="byok-summary-orgs"
        />
        <SummaryCard
          label={lang === "fr" ? "Complètement configurées" : "Fully configured"}
          value={s.fully_configured}
          icon={CheckCircle}
          color="emerald"
          testid="byok-summary-complete"
        />
        <SummaryCard
          label={lang === "fr" ? "Configuration partielle" : "Partial / missing"}
          value={s.partial_or_missing}
          icon={Warning}
          color="rose"
          testid="byok-summary-partial"
        />
        <SummaryCard
          label={lang === "fr" ? "Coût estimé 30j (CAD)" : "Est. cost 30d (CAD)"}
          value={`$${s.total_estimated_cost_cad_30d ?? 0}`}
          icon={ChartLine}
          color="violet"
          testid="byok-summary-cost"
        />
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2">
        {[
          { id: "all", label: lang === "fr" ? "Toutes" : "All" },
          { id: "complete", label: lang === "fr" ? "Configurées" : "Configured" },
          { id: "missing", label: lang === "fr" ? "À configurer" : "Missing" },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`byok-filter-${f.id}`}
            className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
              filter === f.id
                ? "bg-[#002FA7] text-white font-semibold"
                : "bg-secondary text-muted-foreground hover:bg-secondary/70"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-border rounded-xl overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="byok-overview-table">
            <thead className="bg-secondary/40 border-b border-border">
              <tr className="text-left">
                <th className="px-4 py-2 font-semibold">{lang === "fr" ? "Organisation" : "Organization"}</th>
                <th className="px-3 py-2 font-semibold text-center">{lang === "fr" ? "Utilisateurs" : "Users"}</th>
                <th className="px-3 py-2 font-semibold">OpenAI</th>
                <th className="px-3 py-2 font-semibold">AssemblyAI</th>
                <th className="px-3 py-2 font-semibold">OVHcloud</th>
                <th className="px-3 py-2 font-semibold text-right">{lang === "fr" ? "Conso 30j" : "30d usage"}</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrgs.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground text-sm" data-testid="byok-overview-empty">
                    {lang === "fr" ? "Aucune organisation pour ce filtre." : "No organization matches this filter."}
                  </td>
                </tr>
              )}
              {filteredOrgs.map(org => (
                <tr key={org.org_id} className="border-b border-border/60 hover:bg-secondary/20 transition-colors" data-testid={`byok-row-${org.org_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                        org.byok.complete ? "bg-teal-100 text-teal-700" : "bg-rose-100 text-rose-700"
                      }`}>
                        {(org.org_name || "?").slice(0, 1).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate max-w-[200px]" title={org.org_name}>{org.org_name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[200px]" title={org.org_id}>
                          {org.org_slug || org.org_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center text-sm font-medium">{org.users_count}</td>
                  <td className="px-3 py-3"><ServiceCell service="openai" info={org.byok.openai} lang={lang} /></td>
                  <td className="px-3 py-3"><ServiceCell service="assemblyai" info={org.byok.assemblyai} lang={lang} /></td>
                  <td className="px-3 py-3">
                    <ServiceCell service="ovh_storage" info={org.byok.ovh_storage} lang={lang} />
                    {org.byok.ovh_storage?.configured && org.byok.ovh_storage?.bucket_name && (
                      <span className="text-[9px] text-zinc-400 font-mono block mt-0.5">
                        {org.byok.ovh_storage.bucket_name} · {org.byok.ovh_storage.region}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="text-sm font-semibold text-foreground">${org.usage_30d.estimated_cost_cad}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {org.usage_30d.videos} {lang === "fr" ? "vid · " : "vid · "}
                      {org.usage_30d.transcript_minutes} {lang === "fr" ? "min · " : "min · "}
                      {org.usage_30d.qa_generated} Q&R
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tâche 1293 — qui paie quoi : la clé du client, ou la nôtre. */}
      <CostByOrgCard />

      <div className="text-xs text-muted-foreground flex items-center gap-2">
        <Link
          to="/dashboard/settings/ai-package"
          className="inline-flex items-center gap-1 text-[#002FA7] hover:underline"
          data-testid="byok-link-my-settings"
        >
          {lang === "fr" ? "Gérer mes propres clés" : "Manage my own keys"} <ArrowSquareOut size={11} />
        </Link>
        <span>·</span>
        <span>
          {lang === "fr"
            ? `Taux FX USD→CAD utilisé : ${s.fx_rate_usd_cad ?? "—"}`
            : `FX rate USD→CAD: ${s.fx_rate_usd_cad ?? "—"}`}
        </span>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, icon: Icon, color, testid }) {
  const palette = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    emerald: "bg-teal-50 text-teal-700 border-teal-100",
    rose: "bg-rose-50 text-rose-700 border-rose-100",
    violet: "bg-violet-50 text-violet-700 border-violet-100",
  };
  return (
    <div className={`border rounded-xl p-3 ${palette[color] || palette.blue}`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon size={16} weight="duotone" />
        <span className="text-[11px] font-medium opacity-80 uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value ?? "—"}</div>
    </div>
  );
}
